import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GET_MY_PROFILES, CREATE_PROFILE, UPDATE_PROFILE, DELETE_PROFILE, UPDATE_PROFILE_VISIBILITY, UPLOAD_AVATAR } from '../graphql/profile';
import { UPDATE_USER_EMAIL, UPDATE_USER_PASSWORD, UPDATE_USERNAME } from '../graphql/user';
import { FiEdit, FiTrash2, FiPlus, FiImage, FiFileText, FiLock, FiMail, FiSettings, FiEye, FiEyeOff, FiUser, FiLogOut, FiX, FiCheckCircle } from 'react-icons/fi';
import { formatDateTime } from '../utils/dateUtils';
import FileUpload from '../components/FileUpload';
import { getApiUrl } from '../utils/apiConfig';

const Profile = () => {
  const navigate = useNavigate();
  const [editingProfile, setEditingProfile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'TEXT' or 'IMAGE'
  const [formData, setFormData] = useState({
    content: ''
  });

  // 帳號設定相關狀態
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [usernameFormData, setUsernameFormData] = useState({
    newUsername: ''
  });
  const [emailFormData, setEmailFormData] = useState({
    newEmail: '',
    currentPassword: ''
  });
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [accountMessage, setAccountMessage] = useState({ type: '', text: '' });

  const [createProfile] = useMutation(CREATE_PROFILE);
  const [updateProfile] = useMutation(UPDATE_PROFILE);
  const [deleteProfile] = useMutation(DELETE_PROFILE);
  const [updateUsername] = useMutation(UPDATE_USERNAME);
  const [updateUserEmail] = useMutation(UPDATE_USER_EMAIL);
  const [updateUserPassword] = useMutation(UPDATE_USER_PASSWORD);
  const [updateProfileVisibility] = useMutation(UPDATE_PROFILE_VISIBILITY);
  const [uploadAvatar, { loading: uploading }] = useMutation(UPLOAD_AVATAR);
  
  // 圖片上傳相關狀態
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  
  // 打開彈窗
  const openModal = (type) => {
    setModalType(type);
    setFormData({ content: '' });
    setSelectedFile(null);
    setUploadError(null);
    setShowModal(true);
  };
  
  // 關閉彈窗
  const closeModal = () => {
    setShowModal(false);
    setModalType(null);
    setEditingProfile(null);
    setFormData({ content: '' });
    setSelectedFile(null);
    setUploadError(null);
  };

  const { user, updateUser, logout } = useAuth();

  // 檢測是否為 Gmail
  const isGmail = (email) => {
    if (!email) return false;
    const emailLower = email.toLowerCase().trim();
    return emailLower.endsWith('@gmail.com');
  };

  // 處理驗證 Gmail
  const handleVerifyGmail = () => {
    // 先登出
    logout();
    // 然後自動觸發 Google 登入
    const apiUrl = getApiUrl();
    window.location.href = `${apiUrl}/auth/google`;
  };
  
  // 登出處理
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const { loading, error, data, refetch } = useQuery(GET_MY_PROFILES);
  
  // 檢查是否已有文字或圖片
  const profiles = data?.myProfiles?.profiles || [];
  const hasTextProfile = profiles.some(p => p.contentType === 'TEXT');
  const hasImageProfile = profiles.some(p => p.contentType === 'IMAGE');
  
  useEffect(() => {
    if (editingProfile) {
      setFormData({
        content: editingProfile.content
      });
      setModalType(editingProfile.contentType);
      setShowModal(true);
    }
  }, [editingProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 驗證：如果是圖片類型，必須有 content（已上傳的 URL）
    if (modalType === 'IMAGE' && !formData.content) {
      setUploadError('請先上傳圖片');
      return;
    }
    
    // 驗證：如果是文字類型，必須有內容
    if (modalType === 'TEXT' && !formData.content.trim()) {
      alert('請輸入內容');
      return;
    }
    
    try {
      if (editingProfile) {
        // 更新現有資料
        await updateProfile({
          variables: {
            input: {
              id: editingProfile.id,
              content: formData.content,
              visibility: 'PUBLIC', // 自動設定公開
              order: editingProfile.order, // 保持原有排序
              isActive: editingProfile.isActive
            }
          }
        });
      } else {
        // 創建新資料
        await createProfile({
          variables: {
            input: {
              contentType: modalType,
              content: formData.content,
              visibility: 'PUBLIC', // 自動設定公開
              order: modalType === 'TEXT' ? 1 : 0 // 文字排序1，圖片排序0
            }
          }
        });
      }
      
      closeModal();
      await refetch();
    } catch (error) {
      console.error('Profile operation failed:', error);
      setUploadError(error.message || '操作失敗，請稍後再試');
    }
  };

  const handleDelete = async (profileId) => {
    if (window.confirm('確定要刪除這個資料嗎？')) {
      try {
        await deleteProfile({
          variables: { id: profileId }
        });
        await refetch();
      } catch (error) {
        console.error('Delete failed:', error);
        alert('刪除失敗，請稍後再試');
      }
    }
  };
  
  const handleEdit = (profile) => {
    setEditingProfile(profile);
  };

  const handleCancel = () => {
    closeModal();
  };

  // 處理文件選擇
  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setUploadError(null);
  };

  // 處理圖片上傳
  const handleUploadImage = async () => {
    if (!selectedFile) return;

    setUploadError(null);

    try {
      const { data } = await uploadAvatar({
        variables: { file: selectedFile },
        context: {
          headers: {
            'apollo-require-preflight': true
          }
        }
      });

      if (data.uploadAvatar.success) {
        // 將 URL 設置到表單中
        const imageUrl = data.uploadAvatar.url || data.uploadAvatar.filePath;
        setFormData(prev => ({
          ...prev,
          content: imageUrl
        }));
        
        // 清除選中的文件
        setSelectedFile(null);
      } else {
        const errorMsg = data.uploadAvatar.errors?.[0]?.message || data.uploadAvatar.message;
        setUploadError(errorMsg);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.networkError?.statusCode === 404 
        ? '找不到上傳端點，請檢查服務器配置'
        : error.graphQLErrors?.[0]?.message || error.networkError?.message || error.message || '上傳失敗，請稍後再試';
      setUploadError(errorMessage);
    }
  };

  // 處理Email更新
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setAccountMessage({ type: '', text: '' });

    if (!emailFormData.newEmail || !emailFormData.currentPassword) {
      setAccountMessage({ type: 'error', text: '請填寫所有欄位' });
      return;
    }

    try {
      const { data } = await updateUserEmail({
        variables: {
          input: {
            newEmail: emailFormData.newEmail,
            currentPassword: emailFormData.currentPassword
          }
        }
      });

      if (data.updateUserEmail.success) {
        setAccountMessage({ type: 'success', text: 'Email更新成功！' });
        setEmailFormData({ newEmail: '', currentPassword: '' });
        setShowEmailForm(false); // 成功後收起表單
        // 更新AuthContext中的使用者資料
        if (updateUser) {
          updateUser(data.updateUserEmail.user);
        }
      } else {
        const errorMsg = data.updateUserEmail.errors?.[0]?.message || data.updateUserEmail.message;
        setAccountMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Email update error:', error);
      setAccountMessage({ type: 'error', text: '更新Email失敗，請稍後再試' });
    }
  };

  // 處理密碼更新
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setAccountMessage({ type: '', text: '' });

    if (!passwordFormData.currentPassword || !passwordFormData.newPassword || !passwordFormData.confirmPassword) {
      setAccountMessage({ type: 'error', text: '請填寫所有欄位' });
      return;
    }

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setAccountMessage({ type: 'error', text: '新密碼與確認密碼不符' });
      return;
    }

    if (passwordFormData.newPassword.length < 6) {
      setAccountMessage({ type: 'error', text: '新密碼長度至少需要6個字元' });
      return;
    }

    try {
      const { data } = await updateUserPassword({
        variables: {
          input: {
            currentPassword: passwordFormData.currentPassword,
            newPassword: passwordFormData.newPassword
          }
        }
      });

      if (data.updateUserPassword.success) {
        setAccountMessage({ type: 'success', text: '密碼更新成功！' });
        setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false); // 成功後收起表單
      } else {
        const errorMsg = data.updateUserPassword.errors?.[0]?.message || data.updateUserPassword.message;
        setAccountMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Password update error:', error);
      setAccountMessage({ type: 'error', text: '更新密碼失敗，請稍後再試' });
    }
  };

  // 處理 Username 更新
  const handleUpdateUsername = async (e) => {
    e.preventDefault();
    setAccountMessage({ type: '', text: '' });

    if (!usernameFormData.newUsername) {
      setAccountMessage({ type: 'error', text: '請輸入新的 Username' });
      return;
    }

    try {
      const { data } = await updateUsername({
        variables: {
          input: {
            newUsername: usernameFormData.newUsername
          }
        }
      });

      if (data.updateUsername.success) {
        setAccountMessage({ type: 'success', text: 'Username 更新成功！' });
        setUsernameFormData({ newUsername: '' });
        setShowUsernameForm(false);
        // 更新AuthContext中的使用者資料
        if (updateUser) {
          updateUser(data.updateUsername.user);
        }
      } else {
        const errorMsg = data.updateUsername.errors?.[0]?.message || data.updateUsername.message;
        setAccountMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Username update error:', error);
      setAccountMessage({ type: 'error', text: '更新 Username 失敗，請稍後再試' });
    }
  };

  // 處理個人資料可見性切換
  const handleToggleProfileVisibility = async (newValue) => {
    setAccountMessage({ type: '', text: '' });

    try {
      const { data } = await updateProfileVisibility({
        variables: {
          profileOpen: newValue
        }
      });

      if (data.updateProfileVisibility.success) {
        setAccountMessage({ 
          type: 'success', 
          text: newValue ? '已開放個人資料給配對系統' : '已關閉個人資料配對功能' 
        });
        // 更新AuthContext中的使用者資料
        if (updateUser) {
          updateUser(data.updateProfileVisibility.user);
        }
      } else {
        const errorMsg = data.updateProfileVisibility.errors?.[0]?.message || data.updateProfileVisibility.message;
        setAccountMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Profile visibility update error:', error);
      setAccountMessage({ type: 'error', text: '更新設定失敗，請稍後再試' });
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">載入中...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-600">載入失敗: {error.message}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 頁面標題 */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-600 to-purple-600 bg-clip-text text-transparent">個人資料管理</h1>
            <p className="mt-2 text-gray-700">管理你的個人介紹和照片</p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
          >
            <FiLogOut className="mr-2" />
            登出
          </button>
        </div>

        {/* 帳號設定區塊 */}
        <div className="bg-white shadow-lg rounded-lg mb-6 border-2 border-purple-200">
          <div 
            className="px-6 py-4 border-b-2 border-purple-200 flex items-center justify-between cursor-pointer hover:bg-purple-50 transition-colors"
            onClick={() => setShowAccountSettings(!showAccountSettings)}
          >
            <div className="flex items-center">
              <FiSettings className="mr-3 h-5 w-5 text-purple-600" />
              <h3 className="text-lg font-medium text-purple-900">帳號設定</h3>
            </div>
            <span className="text-sm text-gray-500">
              {showAccountSettings ? '收起' : '展開'}
            </span>
          </div>

          {showAccountSettings && (
            <div className="p-6 space-y-6">
              {/* 訊息提示 */}
              {accountMessage.text && (
                <div className={`p-4 rounded-md ${
                  accountMessage.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {accountMessage.text}
                </div>
              )}

              {/* 顯示當前使用者資訊 */}
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm text-gray-600">當前登入帳號</p>
                <p className="text-lg font-medium text-gray-900 mt-1">{user?.username}</p>
                <p className="text-sm text-gray-600 mt-1">Email: {user?.email}</p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">使用者 ID:</span> 
                  <span className="ml-2 font-mono text-xs bg-white px-2 py-1 rounded border">{user?.id}</span>
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  此 ID 可用於添加好友等功能
                </p>
                
                {/* 未驗證帳號的建議 */}
                {!user?.isVerified && (
                  <div className="mt-4 space-y-3">
                    {/* 如果是 Gmail，顯示驗證按鈕 */}
                    {isGmail(user?.email) && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-start space-x-3">
                          <FiCheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-900 mb-2">
                              檢測到 Gmail 帳號
                            </p>
                            <p className="text-xs text-blue-700 mb-3">
                              使用 Google 登入可以快速驗證您的帳號。點擊下方按鈕將自動登出並使用 Google 登入。
                            </p>
                            <button
                              type="button"
                              onClick={handleVerifyGmail}
                              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-md hover:shadow-lg text-sm font-medium"
                            >
                              🔐 驗證 Gmail
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* 如果不是 Gmail，顯示建議改為 Gmail */}
                    {!isGmail(user?.email) && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-800 font-medium mb-2">
                          ⚠️ 您的帳號尚未驗證
                        </p>
                        <p className="text-xs text-yellow-700 mb-2">
                          建議將 Email 改為 Google Gmail，以便快速驗證帳號。
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 配對系統可見性設定 */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-3">
                    {user?.profileOpen ? (
                      <FiEye className="mt-1 h-5 w-5 text-green-600" />
                    ) : (
                      <FiEyeOff className="mt-1 h-5 w-5 text-gray-400" />
                    )}
                    <div className="flex-1">
                      <h4 className="text-md font-medium text-gray-900">配對系統可見性</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {user?.profileOpen 
                          ? '你的個人資料目前對配對系統開放，其他使用者可以看到你的配對分數並與你配對。' 
                          : '你的個人資料目前不對配對系統開放，其他使用者無法看到你的配對分數或與你配對。'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleToggleProfileVisibility(!user?.profileOpen)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                        user?.profileOpen ? 'bg-purple-500' : 'bg-gray-200'
                      }`}
                      role="switch"
                      aria-checked={user?.profileOpen}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          user?.profileOpen ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-800">
                    <strong>提示：</strong>開啟此設定後，其他使用者可以透過配對系統找到你，並查看與你的配對分數。關閉後，你將不會出現在其他使用者的配對建議中。
                  </p>
                </div>
              </div>

              {/* 更改 Username 表單 */}
              <div className="border-t border-gray-200 pt-6">
                <div 
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-3 rounded-md -mx-3"
                  onClick={() => {
                    setShowUsernameForm(!showUsernameForm);
                    if (!showUsernameForm) {
                      setAccountMessage({ type: '', text: '' });
                    }
                  }}
                >
                  <div className="flex items-center">
                    <FiUser className="mr-2 h-5 w-5 text-purple-600" />
                    <h4 className="text-md font-medium text-purple-900">更改 Username</h4>
                  </div>
                  <span className="text-sm text-gray-500">
                    {showUsernameForm ? '收起' : '展開'}
                  </span>
                </div>
                
                {showUsernameForm && (
                  <form onSubmit={handleUpdateUsername} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      新的 Username
                    </label>
                    <input
                      type="text"
                      value={usernameFormData.newUsername}
                      onChange={(e) => setUsernameFormData({ ...usernameFormData, newUsername: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入新的 Username（3-30 字元）"
                      minLength={3}
                      maxLength={30}
                    />
                  </div>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-gradient-to-r from-purple-400 to-purple-500 text-white rounded-lg hover:from-purple-500 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all shadow-md hover:shadow-lg"
                    >
                      更新 Username
                    </button>
                  </form>
                )}
              </div>

              {/* 更改 Email 表單 */}
              {user?.authProvider !== 'google' && !user?.googleId && (
                <div className="border-t border-gray-200 pt-6">
                  <div 
                    className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-3 rounded-md -mx-3"
                    onClick={() => {
                      setShowEmailForm(!showEmailForm);
                      if (!showEmailForm) {
                        setAccountMessage({ type: '', text: '' });
                      }
                    }}
                  >
                    <div className="flex items-center">
                      <FiMail className="mr-2 h-5 w-5 text-purple-600" />
                      <h4 className="text-md font-medium text-purple-900">更改 Email</h4>
                    </div>
                    <span className="text-sm text-gray-500">
                      {showEmailForm ? '收起' : '展開'}
                    </span>
                  </div>
                  
                  {showEmailForm && (
                  <form onSubmit={handleUpdateEmail} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      新的 Email
                    </label>
                    <input
                      type="email"
                      value={emailFormData.newEmail}
                      onChange={(e) => setEmailFormData({ ...emailFormData, newEmail: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入新的Email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      當前密碼（確認身份）
                    </label>
                    <input
                      type="password"
                      value={emailFormData.currentPassword}
                      onChange={(e) => setEmailFormData({ ...emailFormData, currentPassword: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入當前密碼"
                    />
                  </div>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-gradient-to-r from-purple-400 to-purple-500 text-white rounded-lg hover:from-purple-500 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all shadow-md hover:shadow-lg"
                    >
                      更新 Email
                    </button>
                  </form>
                  )}
                </div>
              )}

              {/* Google 登錄使用者提示 */}
              {(user?.authProvider === 'google' || user?.googleId) && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-blue-800">Google 登入帳號</h4>
                        <p className="mt-1 text-sm text-blue-700">
                          您使用 Google 帳號登入，Email 和密碼由 Google 管理，無法在此更改。如需更改，請前往您的 Google 帳號設定。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 更改密碼表單 */}
              {user?.authProvider !== 'google' && !user?.googleId && (
                <div className="border-t border-gray-200 pt-6">
                  <div 
                    className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-3 rounded-md -mx-3"
                    onClick={() => {
                      setShowPasswordForm(!showPasswordForm);
                      if (!showPasswordForm) {
                        setAccountMessage({ type: '', text: '' });
                      }
                    }}
                  >
                    <div className="flex items-center">
                      <FiLock className="mr-2 h-5 w-5 text-purple-600" />
                      <h4 className="text-md font-medium text-purple-900">更改密碼</h4>
                    </div>
                    <span className="text-sm text-gray-500">
                      {showPasswordForm ? '收起' : '展開'}
                    </span>
                  </div>
                  
                  {showPasswordForm && (
                  <form onSubmit={handleUpdatePassword} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      當前密碼
                    </label>
                    <input
                      type="password"
                      value={passwordFormData.currentPassword}
                      onChange={(e) => setPasswordFormData({ ...passwordFormData, currentPassword: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入當前密碼"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      新密碼
                    </label>
                    <input
                      type="password"
                      value={passwordFormData.newPassword}
                      onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入新密碼（至少6個字元）"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      確認新密碼
                    </label>
                    <input
                      type="password"
                      value={passwordFormData.confirmPassword}
                      onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="再次輸入新密碼"
                    />
                  </div>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-gradient-to-r from-purple-400 to-purple-500 text-white rounded-lg hover:from-purple-500 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all shadow-md hover:shadow-lg"
                    >
                      更新密碼
                    </button>
                  </form>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 創建新資料按鈕 */}
        {!showModal && (
          <div className="mb-6 flex gap-3">
            {!hasTextProfile && (
              <button
                onClick={() => openModal('TEXT')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all shadow-md hover:shadow-lg"
              >
                <FiFileText className="mr-2" />
                新增自介
              </button>
            )}
            {!hasImageProfile && (
              <button
                onClick={() => openModal('IMAGE')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all shadow-md hover:shadow-lg"
              >
                <FiImage className="mr-2" />
                新增照片
              </button>
            )}
          </div>
        )}

        {/* 彈窗 */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 ${
              modalType === 'TEXT' ? 'border-yellow-300' : 'border-purple-300'
            }`}>
              {/* 彈窗標題 */}
              <div className={`flex items-center justify-between p-6 border-b-2 ${
                modalType === 'TEXT' ? 'border-yellow-200 bg-yellow-50' : 'border-purple-200 bg-purple-50'
              }`}>
                <h2 className={`text-xl font-semibold ${
                  modalType === 'TEXT' ? 'text-yellow-900' : 'text-purple-900'
                }`}>
                  {editingProfile ? `編輯${modalType === 'TEXT' ? '自介' : '照片'}` : `新增${modalType === 'TEXT' ? '自介' : '照片'}`}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              {/* 彈窗內容 */}
              <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                  {modalType === 'TEXT' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        自介內容
                      </label>
                      <textarea
                        name="content"
                        value={formData.content}
                        onChange={handleInputChange}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="輸入你的個人介紹..."
                        required
                      />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* 文件上傳組件 */}
                      <FileUpload
                        onFileSelect={handleFileSelect}
                        acceptedTypes={['image/*']}
                        maxSize={10485760}
                      />
                      
                      {/* 上傳按鈕 */}
                      {selectedFile && (
                        <button
                          type="button"
                          onClick={handleUploadImage}
                          disabled={uploading}
                          className="px-4 py-2 bg-gradient-to-r from-purple-400 to-purple-500 text-white rounded-lg hover:from-purple-500 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all shadow-md hover:shadow-lg"
                        >
                          {uploading ? '上傳中...' : '上傳圖片'}
                        </button>
                      )}
                      
                      {/* 上傳錯誤訊息 */}
                      {uploadError && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <p className="text-red-600 text-sm">{uploadError}</p>
                        </div>
                      )}
                      
                      {/* 顯示已上傳的 URL */}
                      {formData.content && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-600 mb-2">已上傳的圖片：</p>
                          <img
                            src={formData.content}
                            alt="預覽"
                            className="max-w-full h-auto max-h-64 rounded-lg shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 按鈕 */}
                <div className={`flex justify-end space-x-3 mt-6 pt-4 border-t-2 ${
                  modalType === 'TEXT' ? 'border-yellow-200' : 'border-purple-200'
                }`}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 border border-transparent rounded-lg shadow-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all hover:shadow-lg ${
                      modalType === 'TEXT' 
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 focus:ring-yellow-500' 
                        : 'bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600 focus:ring-purple-500'
                    }`}
                  >
                    {editingProfile ? '更新' : '創建'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 資料列表 */}
        <div className="bg-white shadow-lg rounded-lg border-2 border-purple-200">
          <div className="px-6 py-4 border-b-2 border-purple-200">
            <h3 className="text-lg font-medium bg-gradient-to-r from-purple-600 to-yellow-600 bg-clip-text text-transparent">我的資料</h3>
          </div>
          
          {profiles.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">沒有資料</h3>
              <p className="mt-1 text-sm text-gray-500">開始新增你的個人資料吧！</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {profiles.map((profile) => (
                <div key={profile.id} className="px-6 py-4">
                  {profile.contentType === 'TEXT' ? (
                    // 文字類型：保持原有佈局
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <FiFileText className="h-5 w-5 text-yellow-500" />
                          <span className="text-sm font-medium text-yellow-900">個人自介</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          <p className="whitespace-pre-wrap">{profile.content}</p>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          更新於: {formatDateTime(profile.updatedAt)}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(profile)}
                          className="p-2 text-gray-400 hover:text-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 rounded transition-colors"
                          title="編輯"
                        >
                          <FiEdit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(profile.id)}
                          className="p-2 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded transition-colors"
                          title="刪除"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    // 圖片類型：新的佈局
                    <div>
                      {/* 標題和按鈕行 */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <FiImage className="h-5 w-5 text-purple-500" />
                          <span className="text-sm font-medium text-purple-900">個人照片</span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(profile)}
                            className="p-2 text-gray-400 hover:text-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded transition-colors"
                            title="編輯"
                          >
                            <FiEdit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(profile.id)}
                            className="p-2 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded transition-colors"
                            title="刪除"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* 分隔線 */}
                      <div className="border-b-2 border-purple-200 mb-4"></div>
                      
                      {/* 圖片 */}
                      <div className="w-full overflow-hidden">
                        <img
                          src={profile.content}
                          alt="個人圖片"
                          className="w-full h-auto max-w-full rounded-lg shadow-sm object-contain"
                          style={{ maxHeight: '600px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const errorDiv = e.target.nextElementSibling;
                            if (errorDiv) {
                              errorDiv.style.display = 'block';
                            }
                          }}
                        />
                        <div className="hidden bg-gray-100 rounded-lg p-4 text-center">
                          <FiImage className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">圖片載入失敗</p>
                        </div>
                      </div>
                      
                      {/* 更新時間 */}
                      <div className="mt-3 text-xs text-gray-500">
                        更新於: {formatDateTime(profile.updatedAt)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile; 